import { useEffect, useState } from "react";

import useQueryParams from "./useQueryParams";

export type TOptions = {
    perPage: number;
    zeroShift?: number;
    loop?: boolean;
    withScrollUp?: boolean;
    step?: number;
    saveToSearch?: boolean;
};

export type TPagination = {
    visibleItems: any[];
    getPageByIndex: (index: number) => any[];
    perPage: number;
    currentPage: number;
    pagesCount: number;
    totalCount: number;
    handlePerPageChange: (newValue: number) => void;
    handleNextPage: () => void;
    handlePrevPage: () => void;
    handlePageNavigation: (newValue: number) => void;
    isFirstPage: boolean;
    isLastPage: boolean;
};

const useSearchSync = (state: Record<string | number, { value: string | number; setValue: (newValue: string) => void }>) => {
    const { params = {}, setByKey, setParams } = useQueryParams();

    useEffect(() => {
        Object.keys(state).forEach((key) => {
            const { value } = state[key];
            if (params[key] !== `${value}`) {
                if (value !== 0) {
                    setByKey(key, value);
                } else {
                    if (+params?.[key] !== 0) {
                        state?.[key]?.setValue(params?.[key]);
                    }
                }
            }
        });
    }, [JSON.stringify(state)]);

    useEffect(() => {
        Object.keys(params).forEach((key) => {
            if (state?.[key]?.value !== `${params?.[key]}`) {
                state?.[key]?.setValue(params?.[key]);
            }
        });
    }, [JSON.stringify(params)]);
};

const useSwitchableState = (defaultValue: number, type: "react" | "search", key: string, delta: number = 0): [number, (newValue: number) => void] => {
    const [stateValue, setStateValue] = useState<number>(defaultValue + delta);

    const { params = {}, setByKey } = useQueryParams();

    const searchValue: number = isNaN(+params?.[key]) ? 0 : +params?.[key] - delta;
    const setSearchValue = (newValue: number) => {
        setByKey(key, newValue + delta);
    };

    return type === "search" ? [searchValue, setSearchValue] : [stateValue, setStateValue];
};

const usePagination = (items = [], options: TOptions): TPagination => {
    const { zeroShift = 0, loop = false, withScrollUp = false, step, saveToSearch = true } = options ?? {};

    const [perPage, setPerPage] = useSwitchableState(0, "react", "perPage");
    const [currentPage, setCurrentPage] = useSwitchableState(0, saveToSearch ? "search" : "react", "currentPage", 1);

    const totalCount = items?.length;
    const pagesCount = Math.ceil(totalCount / (perPage || 1));

    useEffect(() => {
        setPerPage((options?.perPage ?? 1) || 1);
    }, [options?.perPage]);

    useEffect(() => {
        if (withScrollUp) {
            try {
                (document as any).getElementById(`scrollWrapper`).scrollTo({ top: 0, behavior: "auto" });
            } catch (error) {}
            try {
                window.scrollTo({ top: 0, behavior: "auto" });
            } catch (error) {}
        }
    }, [currentPage]);

    useEffect(() => {
        if (!saveToSearch) {
            setCurrentPage(Math.max(Math.min(currentPage, pagesCount - 1), 0));
        }
    }, [perPage]);

    return {
        visibleItems: items?.slice?.(perPage * currentPage, perPage * (currentPage + 1)) ?? [],
        getPageByIndex: (index: number) => {
            return items?.slice?.(perPage * index, perPage * (index + 1)) ?? [];
        },
        perPage,
        currentPage: currentPage + zeroShift,
        pagesCount,
        totalCount: items?.length,
        handlePerPageChange: (newValue: number) => {
            setPerPage(Math.max(newValue, 0));
        },
        handleNextPage: () => {
            if (loop) {
                setCurrentPage((currentPage + 1) % pagesCount);
            } else {
                setCurrentPage(Math.min(currentPage + 1, pagesCount - 1));
            }
        },
        handlePrevPage: () => {
            if (loop) {
                setCurrentPage((currentPage - 1 + pagesCount) % pagesCount);
            } else {
                setCurrentPage(Math.max(currentPage - 1, 0));
            }
        },
        handlePageNavigation: (newValue: number) => {
            setCurrentPage(Math.min(Math.max(newValue - zeroShift, 0), pagesCount));
        },
        isFirstPage: currentPage === 0,
        isLastPage: currentPage === pagesCount - 1,
    };
};

export default usePagination;
